/**
 * Authentication state.
 *
 * TOKEN STORAGE (see ADMIN_WEB_ARCHITECTURE.md §5 for the full reasoning):
 *   - access token  -> memory only. Never written to disk.
 *   - refresh token -> sessionStorage. Dies with the tab; survives a reload.
 *
 * The backend returns tokens in the response body and sets no cookies, so an
 * HTTP-only cookie is not available to us. This is the least-bad option, and the
 * residual XSS risk is recorded in BACKEND_GAPS.md rather than glossed over.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { configureApiClient } from '@/services/api/client'
import { authApi } from '@/services/api/auth'
import type { TenantPublic, TokenBundle, UserIdentity } from '@/types/api'

const REFRESH_KEY = 'tf.refresh'

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

export interface AuthState {
  status: AuthStatus
  user: UserIdentity | null
  tenant: TenantPublic | null
  role: string | null
}

export interface AuthContextValue extends AuthState {
  /** Store a freshly issued token pair and mark the session authenticated. */
  establishSession: (bundle: TokenBundle) => void
  signOut: () => Promise<void>
  /** Switch tenant: new tokens, then the caller clears the query cache. */
  switchTenant: (tenantId: string) => Promise<TokenBundle>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function readRefreshToken(): string | null {
  try {
    return sessionStorage.getItem(REFRESH_KEY)
  } catch {
    return null
  }
}

function writeRefreshToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(REFRESH_KEY, token)
    else sessionStorage.removeItem(REFRESH_KEY)
  } catch {
    // Storage blocked: the session simply will not survive a reload.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    user: null,
    tenant: null,
    role: null,
  })

  // Held in a ref, not state: the API client reads it synchronously on every
  // request, and it must never trigger a re-render.
  const accessToken = useRef<string | null>(null)

  const applyBundle = useCallback((bundle: TokenBundle) => {
    accessToken.current = bundle.access_token
    writeRefreshToken(bundle.refresh_token)
    setState({
      status: 'authenticated',
      user: bundle.user,
      tenant: bundle.tenant,
      role: bundle.role,
    })
  }, [])

  const clearSession = useCallback(() => {
    accessToken.current = null
    writeRefreshToken(null)
    setState({ status: 'unauthenticated', user: null, tenant: null, role: null })
  }, [])

  // Wire the API client exactly once, before anything can issue a request.
  // Handlers close over refs, so they never go stale.
  const wired = useRef(false)
  if (!wired.current) {
    wired.current = true
    configureApiClient({
      getAccessToken: () => accessToken.current,
      refreshTokens: async () => {
        const token = readRefreshToken()
        if (!token) return null
        try {
          const bundle = await authApi.refresh(token)
          accessToken.current = bundle.access_token
          writeRefreshToken(bundle.refresh_token)
          return bundle.access_token
        } catch {
          // Rotation means a failed refresh is terminal — the old token is gone.
          writeRefreshToken(null)
          accessToken.current = null
          return null
        }
      },
      onUnauthorized: () => {
        accessToken.current = null
        writeRefreshToken(null)
        setState({ status: 'unauthenticated', user: null, tenant: null, role: null })
      },
    })
  }

  // Restore a session on load. Until this resolves the router shows a skeleton and
  // mounts no route, so a protected page never paints for an unauthenticated user.
  //
  // This MUST run exactly once. Refresh tokens rotate: the backend revokes the old
  // one as it issues the new pair. StrictMode double-invokes effects in development,
  // and a second restore would present the already-revoked token, get a 401, and log
  // the user out on every reload. A ref-guarded promise makes the call single-flight
  // across both invocations rather than merely ignoring the second result.
  const restorePromise = useRef<Promise<void> | null>(null)

  useEffect(() => {
    restorePromise.current ??= (async () => {
      const token = readRefreshToken()
      if (!token) {
        setState((current) => ({ ...current, status: 'unauthenticated' }))
        return
      }
      try {
        applyBundle(await authApi.refresh(token))
      } catch {
        clearSession()
      }
    })()

    void restorePromise.current
  }, [applyBundle, clearSession])

  const signOut = useCallback(async () => {
    const token = readRefreshToken()
    // Clear locally first: the user is signed out whether or not the call lands.
    clearSession()
    if (token) {
      try {
        await authApi.logout(token)
      } catch {
        // Best effort — the local session is already gone.
      }
    }
  }, [clearSession])

  const switchTenant = useCallback(
    async (tenantId: string) => {
      const bundle = await authApi.switchTenant(tenantId)
      applyBundle(bundle)
      return bundle
    },
    [applyBundle],
  )

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, establishSession: applyBundle, signOut, switchTenant }),
    [state, applyBundle, signOut, switchTenant],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}
